import { useParams } from "next/navigation";
import { deviceDetector } from "@ospweb/sdk-ui";
import { useEffect, useMemo, useState } from "react";

import { resolveMobilePricingCta } from "./ctaMatrix";
import { buildPricingOptions } from "./options";
import {
  resolveMobilePricingSelection,
  resolvePricingOptionsDisplay,
  type SupportedMobilePricingPlanType,
} from "./selection";
import type { SubscriptionMobilePricingProps } from "./types";
import { useMemberCheckout } from "@components/Member/Subscription/useMemberCheckout";
import { useMemberCompletePlans } from "@/src/hooks/useMemberCompletePlans";
import { BillingCycleType } from "../Subscription/types";

export const useSubscriptionMobilePricingViewModel = ({
  data,
  trialList,
  userPlanInfo,
  planIdParam,
}: Pick<
  SubscriptionMobilePricingProps,
  "data" | "trialList" | "userPlanInfo" | "planIdParam"
>) => {
  const { locale } = useParams<{ locale?: string }>();
  const initialSelection = useMemo(
    () =>
      resolveMobilePricingSelection({
        planIdParam,
        data,
        userPlanInfo,
      }),
    [planIdParam, data, userPlanInfo],
  );
  const [selectedPlanType, setSelectedPlanType] =
    useState<SupportedMobilePricingPlanType>(initialSelection.selectedPlanType);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setSelectedPlanType(initialSelection.selectedPlanType);
    setIsExpanded(false);
  }, [initialSelection]);

  const completePlans = useMemberCompletePlans({ data });
  const { isPaying, handleCheckout } = useMemberCheckout({ data });

  const selectedPlan = completePlans[selectedPlanType];
  const selectedBillingCycle = selectedPlan?.pay_cycle as BillingCycleType;
  const isIOSApp = deviceDetector.isIOSApp();

  const pricingOptions = useMemo(
    () =>
      buildPricingOptions({
        selectedWeeklyPlan: completePlans.weekly,
        selectedMonthlyPlan: completePlans.monthly,
        selectedYearlyPlan: completePlans.yearly,
        selectedLifetimePlan: completePlans.lifetime,
        trialList,
        isMember: userPlanInfo?.isMember,
        isIOSApp,
        locale,
      }),
    [completePlans, isIOSApp, locale, trialList, userPlanInfo?.isMember],
  );

  const {
    orderedPricingOptions,
    displayedPricingOptions,
    shouldShowMorePlans,
    shouldShowLessPlans,
  } = useMemo(
    () =>
      resolvePricingOptionsDisplay({
        pricingOptions,
        userPlanInfo,
        isExpanded,
      }),
    [pricingOptions, userPlanInfo, isExpanded],
  );

  const selectedOption = orderedPricingOptions.find(
    (option) => option.key === selectedPlanType,
  );

  const ctaResult = useMemo(
    () =>
      resolveMobilePricingCta({
        locale,
        userPlanInfo,
        selectedPlanType,
        selectedOptionHasTrialBadge: !!selectedOption?.trialBadgeText,
        hasTrialAvailable: (trialList?.length || 0) > 0,
        isIOSApp,
      }),
    [
      isIOSApp,
      locale,
      selectedOption?.trialBadgeText,
      selectedPlanType,
      trialList?.length,
      userPlanInfo,
    ],
  );

  const handlePlanTypeChange = (planType: SupportedMobilePricingPlanType) => {
    setSelectedPlanType(planType);
  };

  return {
    selectedPlanType,
    setSelectedPlanType: handlePlanTypeChange,
    displayedPricingOptions,
    shouldAnimatePriceOnTabSwitch: !isExpanded,
    shouldShowMorePlans,
    shouldShowLessPlans,
    expandPlans: () => setIsExpanded(true),
    collapsePlans: () => setIsExpanded(false),
    selectedPlan,
    selectedBillingCycle,
    isPaying,
    handleCheckout,
    ctaMode: ctaResult.mode,
    ctaText: ctaResult.text,
    showCrypto: ctaResult.showCrypto,
  };
};
